import { App, type FormInstance } from "antd";
import { type PatientTransactionOptions, usePatientTransaction } from "api/clients/usePatientTransaction";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { basisPointsFromDollars } from "@vyne-shared/utils/currency";
import { createElement } from "react";
import { isValidPhone } from "@vyne-shared/utils/phone";
import { validateEmail } from "@vyne-shared/utils/email";
import type { ExistingPatient } from "../existingPatient";
import type { HookAPI } from "antd/es/modal/useModal";
import type { PatientUpdateForm, PatientUpdateRequest } from "api/requests";

/**
 * Build a Patient Update Request to submit to the API.
 * @param paymentForm The form containing user data to submit.
 * @param existingPrp Existing Patient/Responsible Party being updated, if any.
 * @param operation The form submission operation to perform.
 * @returns Request data, or undefined if the form is not ready for submission.
 */
async function buildRequest(
    paymentForm: FormInstance<PatientUpdateForm>,
    existingPrp: ExistingPatient,
    operation: string,
) {
    let values: PatientUpdateForm;
    try {
        // Perform validation first - this may throw an error if fields are invalid, which is fine.
        values = await paymentForm.validateFields();
    } catch {
        // If validation fails, it will display error messages for the user, so we don't need to do anything else.
        return undefined;
    }

    return {
        // Start with all the form data
        ...values,

        // Set patient IDs from existing patient, if present
        id: existingPrp?.id,
        pmsId: existingPrp?.pmsId,

        // Clear invalid contact details (generally, this only happens when the invalid contact detail came from the PMS)
        // Note that this function for single requests (not bulk requests)
        phone: ensureValidPhone(values.phone),
        email: ensureValidEmail(values.email),

        // Have to convert units
        amount: basisPointsFromDollars(values.amount),

        // If you give the Buttons a name and value like you do in normal HTML to include them in the form data,
        // React and/or antd will just throw that away, so we're stuck manually re-implementing a kludge for
        // something that worked perfectly fine to begin with.
        sendNotification: operation === "sendRequest",
    } satisfies PatientUpdateRequest;
}

/* If phone valid, return it unchanged. If invalid, return undefined. */
function ensureValidPhone(phone: string | undefined) {
    return phone && isValidPhone(phone) ? phone : undefined;
}

/* If email address valid, return it unchanged. If invalid, return undefined. */
function ensureValidEmail(email: string | undefined) {
    return validateEmail(email) ? email : undefined;
}

// doing it this way so we don't need JSX parsing on this file
const icon = createElement(QuestionCircleOutlined);

/**
 * Confirm a Patient Update Request is ready for API submission.
 * @param request The request to examine.
 * @returns Promise containing boolean flag indicating if request can be submitted.
 */
async function confirmRequest(request: PatientUpdateRequest, modal: HookAPI) {
    if (request.sendNotification && !request.allowCommunication) {
        const name = [request.givenName, request.familyName].filter((x) => typeof x === "string").join(" ");
        return await modal.confirm(confirmPatientCommunicationModalConfig(name));
    } else {
        return true;
    }
}

/**
 * Prompt the user to confirm whether they want to communicate with a P/RP when Allow Communication has been disabled.
 * @param name The name of the P/RP.
 * @returns Promise containing boolean flag indicating if request can be submitted.
 */
export function confirmPatientCommunicationModalConfig(name: string) {
    return {
        title: "Are you sure you want to send a message?",
        icon,
        content: `You have configured ${name} not to receive communications, but you are attempting to send a message anyway. Are you sure you want ${name} to receive a notification?`,
        okText: "Yes",
        cancelText: "No",
    };
}

/**
 * Hook to submit Patient Update Requests using data supplied by a form.
 * @param paymentForm The form to obtain data from.
 * @param existingPrp The existing Patient/Responsible Party we are updating, or undefined if this is a new P/RP.
 * @param options Options to configure the submission behavior.
 * @returns React Query mutation properties, but with a submit function instead of a mutate function.
 */
export function useFormSubmission(
    paymentForm: FormInstance<PatientUpdateForm>,
    existingPrp: ExistingPatient,
    options: Omit<PatientTransactionOptions, "mutationFn">,
) {
    const { mutate, isPending } = usePatientTransaction(options);
    const { modal } = App.useApp();

    const submit = async (operation: string) => {
        const request = await buildRequest(paymentForm, existingPrp, operation);
        if (request) {
            const confirmed = await confirmRequest(request, modal);
            if (confirmed) {
                mutate(request);
            }
        }
    };

    return { isPending, submit } as const;
}
